import { FileTypeENUM } from "@/widgets/file-uploader-widget/interface";
import { atom } from "recoil";
import { IPlcReport } from "./plc-report.types";

export type { IPlcReport, IPreflightReport, IPreflightReportItem } from "./plc-report.types";

export interface IBoardMissionsFile {
  id: string;
  name: string;
  screenShotURL: string;
  uploadDate: string;
  filePath: string;
  fileType?: FileTypeENUM;
  bucketKey?: string;
  boardMissionId?: string;
  plcReport?: IPlcReport;
  sku?: string | null;
  /** Multiple type/SKU links on one file (preferred over single `sku`). */
  skus?: string[];
  fileSize?: number;
}

const asTrimmedString = (value: unknown): string => {
  if (typeof value === "string") {
    return value.trim();
  }
  if (typeof value === "number") {
    return String(value);
  }
  return "";
};

const normalizeFileSkus = (file: Record<string, unknown>): string[] => {
  const rawSkus = file.skus ?? file.Skus;
  if (Array.isArray(rawSkus)) {
    return rawSkus
      .map((value) => asTrimmedString(value))
      .filter(Boolean);
  }
  if (typeof rawSkus === "string" && rawSkus.trim()) {
    return rawSkus
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);
  }
  const single = asTrimmedString(file.sku ?? file.Sku);
  return single ? [single] : [];
};

export const resolveBoardMissionsFileBucketKey = (
  file: Pick<IBoardMissionsFile, "bucketKey" | "filePath">
): string => asTrimmedString(file.bucketKey || file.filePath);

const normalizeBoardMissionsFile = (raw: unknown): IBoardMissionsFile => {
  const file =
    raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const bucketKey =
    asTrimmedString(file.bucketKey || file.BucketKey) || undefined;
  const skus = normalizeFileSkus(file);
  return {
    ...(file as unknown as IBoardMissionsFile),
    id: asTrimmedString(file.id || file.Id),
    name: asTrimmedString(
      file.name || file.Name || file.fileName || file.FileName
    ),
    screenShotURL: asTrimmedString(
      file.screenShotURL ||
        file.ScreenShotURL ||
        file.screenshotUrl ||
        file.ScreenshotUrl
    ),
    uploadDate: asTrimmedString(file.uploadDate || file.UploadDate),
    filePath: asTrimmedString(file.filePath || file.FilePath),
    fileType: (file.fileType ?? file.FileType) as FileTypeENUM | undefined,
    bucketKey,
    boardMissionId:
      asTrimmedString(file.boardMissionId || file.BoardMissionId) || undefined,
    plcReport: (file.plcReport || file.PlcReport) as IPlcReport | undefined,
    sku: skus[0] || asTrimmedString(file.sku ?? file.Sku) || null,
    skus,
    fileSize:
      typeof file.fileSize === "number"
        ? file.fileSize
        : typeof file.FileSize === "number"
          ? file.FileSize
          : typeof file.size === "number"
            ? file.size
            : undefined,
    
  };
};

/** Gateway wraps jobs files as `{ data: File[] }` via httpResponse.ok. */
export const unwrapBoardMissionsFiles = (data: unknown): IBoardMissionsFile[] => {
  let current: unknown = data;
  for (let depth = 0; depth < 4; depth++) {
    if (Array.isArray(current)) {
      return current.map(normalizeBoardMissionsFile);
    }
    if (!current || typeof current !== "object") {
      return [];
    }
    current = (current as { data?: unknown }).data;
  }
  return [];
};

export const boardMissionsFilesState = atom<IBoardMissionsFile[]>({
  default: [],
  key: "boardMissionsFilesState",
});
